import React from 'react';
import cc from 'classcat';
import { NextPageContext } from 'next';

import { Breadcrumbs, CardItem, EventButton, HeroStatic } from '../../../../components';
import { useSiteQuery } from '../../../../hooks';
import { getEventDateStr } from '../../../../lib/EventDate';
import { postDate } from '../../../../lib/PostDate';
import ErrorPage from '../../../_error';
import NotFoundPage from '../../../404';

import DateIcon from '../../../../images/icons/date.svg';
import PinIcon from '../../../../images/icons/pin.svg';

import query from './index.graphql';

import styles from './index.module.scss';

interface Props {
  slug: string;
}

const renderEventInfo = (event: EventFragment) => {
  if (event) {
    const date = event.eventDate?.[0];
    const address = event.postalAddress!.replace('\n', '<br />');
    return (
      <div className={styles.event}>
        {date && (
          <div className={styles.info}>
            <DateIcon />
            {getEventDateStr(date)}
          </div>
        )}
        <div className={styles.info}>
          <PinIcon />
          <span dangerouslySetInnerHTML={{ __html: address }} />
        </div>
        <div className={styles.button}>
          <EventButton title={event.title!} />
        </div>
      </div>
    );
  }
  return null;
};

const ArticlePage = ({ slug }: Props) => {
  const [{ data, error }] = useSiteQuery<ArticleQuery>({ query, variables: { slug } });

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entry;
  const entries = data?.entries;

  if (entry) {
    const title = entry.title!;
    const uri = entry.uri!;
    const heroImage = entry.heroImage;

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'About Us', uri: '/about-us' },
      { title: 'Information, News & Events', uri: '/about-us/news-and-events' },
      { title, uri },
    ];

    return (
      <div>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {heroImage && (
          <HeroStatic photos={heroImage} title={title}>
            <div className="sectionHeading">Information, News & Events</div>
            <h1 className="heroH1" data-test-id="customTitle">
              {title}
            </h1>
            <span className={cc(['postDate', styles.postDate])}>{postDate(entry.postDate!)}</span>
            <p className="mt-1 mt-lg-2 heroHeadline" dangerouslySetInnerHTML={{ __html: entry.description! }} />
            {entry.__typename === 'articles_event_Entry' && renderEventInfo(entry)}
          </HeroStatic>
        )}
        <div className="section">
          <div className="container">
            <div className="articleBody">
              <div dangerouslySetInnerHTML={{ __html: entry.body! }} />
              {entry.__typename === 'articles_event_Entry' && (
                <div className={styles.bottom}>
                  <EventButton title={entry.title!} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <h2 className="center">More Information, News & Events</h2>
            <div className="row cardsWrapper">
              {entries
                ?.filter((item) => item?.slug !== entry.slug!)
                .slice(0, 3)
                .map((item) => item && <CardItem key={item.uid!} item={item} />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <NotFoundPage />;
};

ArticlePage.getInitialProps = ({ req }: NextPageContext) => ({
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1).replace(/\?.*$/, ''),
});

export default ArticlePage;
